import React, { useRef } from 'react';

import { userKey } from 'queryKeys';

import { styled, MarkdownRender } from '@gogame/components';
import { fromUTCToTimeZone } from '@gogame/intl-ts';
import { useQuery, useMutation, useQueryClient } from '@gogame/query';
import { useScreenMessage, MessageProvider } from '@gogame/screens';
import { useSocket } from '@gogame/sockets';

import { loadSystemMessages, closeSystemMessage } from './sources';

const SYSTEM_MESSAGES = 'systemMessages';
const EVENT_NEW_SYSTEM_MESSAGE = 2;

const MarkdownContainer = styled('div')`
  ol,
  ul,
  p {
    margin: 0;
  }
`;

// TODO: check console on warnings
const SystemMessages = () => {
  const api = useScreenMessage();

  const socket = useSocket();

  const queryClient = useQueryClient();
  const visibleMessagesRef = useRef(null);

  if (!visibleMessagesRef.current) {
    visibleMessagesRef.current = new Map();
  }
  const queryKey = userKey.all(SYSTEM_MESSAGES);

  const { data: systemMessages = [] } = useQuery(queryKey, (_, { signal }) =>
    loadSystemMessages({}, { signal })
  );

  const removeMutation = useMutation(closeSystemMessage, {
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries(SYSTEM_MESSAGES);
      const previousValue = queryClient.getQueryData(SYSTEM_MESSAGES);

      queryClient.setQueryData(SYSTEM_MESSAGES, (old) => {
        const next = old?.filter((message) => message.id !== id);
        return next;
      });

      return previousValue;
    },
    onError: (_, __, previousValue) =>
      queryClient.setQueryData(SYSTEM_MESSAGES, previousValue),
  });

  React.useEffect(() => {
    const visible = systemMessages; //.slice(0, 3);
    const visibleIds = visible.map(({ id }) => id);
    visible.forEach(({ id, message, expirationDate }) => {
      const hasExpirationDate = expirationDate !== undefined;
      const autoHideDuration = hasExpirationDate
        ? Math.max(0, fromUTCToTimeZone(expirationDate) - Date.now())
        : null;

      if (autoHideDuration !== 0) {
        if (visibleMessagesRef.current.has(id)) {
          visibleMessagesRef.current.get(id).update(
            <MarkdownContainer as={'div'}>
              <MarkdownRender value={message} />
            </MarkdownContainer>,
            {
              ttl: autoHideDuration,
              preventDuplicate: true,
            }
          );
        } else {
          const snackbar = api.showWarningMessage(
            <MarkdownContainer as={'div'}>
              <MarkdownRender value={message} />
            </MarkdownContainer>,
            {
              key: id,
              preventDuplicate: true,
              ttl: autoHideDuration,
              onClose: (_, reason) => {
                if (['timeout', 'instructed'].includes(reason)) {
                  visibleMessagesRef.current.delete(id);
                  removeMutation.mutate({ id });
                } else if (reason === 'maxsnack') {
                  removeMutation.mutate({ id });
                }
              },
            }
          );

          visibleMessagesRef.current.set(id, snackbar);
        }
      }
    });

    [...visibleMessagesRef.current].forEach(([id, snackbar]) => {
      if (!visibleIds.includes(id)) {
        snackbar.dismiss();
      }
    });
  }, [systemMessages]);

  React.useEffect(() => {
    const handleReceiveNewNotification = () => {
      queryClient.invalidateQueries(SYSTEM_MESSAGES);
    };
    socket.on(EVENT_NEW_SYSTEM_MESSAGE, handleReceiveNewNotification);
    return () => {
      socket.off(EVENT_NEW_SYSTEM_MESSAGE, handleReceiveNewNotification);
    };
  }, [socket, queryClient]);

  return null;
};

export default () => {
  return (
    <MessageProvider
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
    >
      <SystemMessages />
    </MessageProvider>
  );
};
